<script>
	/**
	 * Note that a message and a template are synonymous i.e. the same type of object/model - we just refer to them
	 * differently depending on the context.
	 * @author M.G.
	 */
	import {External, M} from '../../../external';
	import {showScheduledMessagesList} from './ScheduledMessagePicker.svelte';
	import DateTimePicker from '../../../Common/DateTimePicker.svelte';
	import {onDestroy, onMount} from 'svelte';
	import ImagePicker from '../../../Common/ImagePicker.svelte';
	import {
		appendProperty, connectSettings,
		getChannelMessageMaxLength,
		makeRequest,
		toggleAttachments, validateWhatsAppMessage
	} from './common';
	import AddWhatsAppTemplate from './AddWhatsAppTemplate.svelte';

	export let popup, contact, message, utm, onChange, showDate = false, enableScheduling = false, enablePolling = true, target;

	let datePicker, charCount = 0;
	let templates, selectedTemplateId, hideTemplateSelect = true;
	let isSendAllowed, isNewTemplate;
	let pollingTimer, pollCount = 0;
	let isMessagingEnabled = External.Website.isMessagingEnabled();
	let textarea;
	let freeFormMode = false;

	const TEMPLATE_STATUS_LABELS = {
		[External.WHATSAPP_TEMPLATE_STATUS.PENDING]: {label: M.PENDING_APPROVAL},
		[External.WHATSAPP_TEMPLATE_STATUS.APPROVED]: {label: M.APPROVED_TEMPLATES},
		[External.WHATSAPP_TEMPLATE_STATUS.REJECTED]: {label: M.REJECTED_TEMPLATES}
	};

	const WILDCARD = '*';

	if (!message) {
		initNewMessage();

		if (showDate) {
			showDatePicker();
		}
	}

	if (!message.body) {
		message.body = '';
	}

	if (contact) {
		message.contactId = contact.id;

		if(contact.lastMessageReceivedTime) {
			freeFormMode = isDateWithin24Hours(contact.lastMessageReceivedTime);
		}
	}

	function isDateWithin24Hours(date) {
		const now = new Date();
		const twentyFourHoursAgo = new Date(((now.getTime() - (2 * 60 * 1000)) - 24 * 60 * 60 * 1000) ); //2 minute buffer
		return date > twentyFourHoursAgo && date <= now;
	}

	function initNewMessage() {
		message = newEmptyMessage();
	}

	function newEmptyMessage() {
		return {
			id: null,
			body: '',
			originalBody: '',
			originalAttachments: null,
			attachments: null,
			sendTime: null,
			channel: External.CRM_MESSAGE_CHANNEL.WHATSAPP
		};
	}

	function clearEditor() {
		//datePicker.value = '';
		message.body = '';
		message.sendTime = null;
		message.id = null;
		message.attachments = null;
	}

	async function selectProperty() {
		message.body = await appendProperty(message, textarea); //the reassignment triggers the svelte reactivity
	}

	function enableAttachments() {
		message.attachments = toggleAttachments(message);
	}

	function setAttachmentUrl(attachment) {
		attachment.url = External.getFileFromBase(attachment.imageBase) + attachment.image;
	}

	function sendTest() {
		confirmRequest('sendTestMessage', M.SEND_TEST_MESSAGE, M.CONFIRM_SEND_TEST_MESSAGE_DESC, M.TEST_MESSAGE_SENT);
	}

	function sendNow() {
		confirmRequest('sendMessage', M.SEND_MESSAGE, M.CONFIRM_SEND_MESSAGE_DESC, M.MESSAGE_SENT);
	}

	function scheduleMessage() {
		if (message.sendTime.getTime() <= new Date().getTime()) {
			sendScheduledNow();
			return;
		}

		const dateFormat = External.WebsiteSettings.getDateFormat();

		const prompt = M.CONFIRM_SCHEDULE_MESSAGE_DESC
		.replace('01/01/1970',
			External.Tools.dateToStr(message.sendTime, dateFormat))
		.replace('00:00',
			External.Tools.timeToString(message.sendTime, dateFormat === 'uk'));

		if (message.id) {                                                                                   //existing scheduled message
			confirmRequest('updateScheduledMessage', M.UPDATE_MESSAGE, prompt, M.SCHEDULED_MESSAGE_UPDATED);
		} else {                                                                                            //else create new scheduled message
			confirmRequest('scheduleMessage', M.SCHEDULE_MESSAGE, prompt, M.MESSAGE_SUCCESSFULLY_SCHEDULED);
		}
	}

	function sendScheduledNow() {
		confirmRequest('sendScheduledMessage', M.SEND_MESSAGE, M.CONFIRM_SEND_MESSAGE_DESC, M.MESSAGE_SENT);
	}

	function confirmRequest(requestAction, promptHeading, promptDesc, successMsg) {
		try {
			validateWhatsAppMessage(message);
		} catch (e) {
			External.Tools.showError(e);
			return;
		}

		if (contact) {
			message.contactId = contact.id;
		}

		External.Tools.prompt(promptHeading, promptDesc, () => {
			//TODO remove originalAttachments
			External.Tools.makeWebsiteRequest({
				action: requestAction,
				method: 'post',
				data: {
					message: JSON.stringify(message),
					channel: External.CRM_MESSAGE_CHANNEL.WHATSAPP,
					utm: JSON.stringify(utm)
				},
				onOk: () => {
					External.Tools.showAutohideMessage(5, successMsg);
					if (popup) {
						popup.close();
					}
				}
			});
		});
	}

	function cancel() {
		if (message.body && !message.id) {
			External.Tools.prompt(M.DISCARD_MESSAGE, M.DISCARD_MESSAGE_DESC, () => {
				popup.close();
			});
			return;
		}
		popup.close();
	}

	function showDatePicker() {
		if (!message.sendTime) {
			const date = new Date();  //TODO set on msg
			date.setDate(date.getDate() + 1);
			message.sendTime = date;
			External.$('#datePicker').click();
		}
	}

	function hideDatePicker() {
		message.sendTime = null;
	}

	function dateChanged(value) {
		message.sendTime = new Date(value);
	}

	async function showScheduledMessages() {
		await showScheduledMessagesList(contact, External.CRM_MESSAGE_CHANNEL.WHATSAPP, setEditorData, clearEditor, () => {
			External.Tools.prompt(M.SCHEDULED_MESSAGES, M.NO_MESSAGES_SCHEDULED, () => {
				initNewMessage();
				showDatePicker();
				if (templates?.size) {
					selectedTemplateId = '0';
					hideTemplateSelect = false;
				}
			});
		});
	}

	export async function setEditorData(msg) {
		if (msg) {
			message = msg;
			message.originalBody = message.body;
			message.originalAttachments = message.attachments;
		}
		return true;
	}

	async function loadTemplates() {
		const allTemplates = await makeRequest('getWhatsAppTemplates', {}).catch((e) => {
			console.log('unable to load templates: ' + e);
		});

		if (!allTemplates?.length) {
			return;
		}

		templates = new Map(); //store the templates as arrays in a map, keyed/grouped by status (Approved, Pending, Rejected)

		for (let i = 0; i < allTemplates.length; i++) {
			const template = allTemplates[i];
			template.originalBody = template.body;
			template.originalAttachments = template.attachments?.length ? [...template.attachments] : null;
			if (!templates.get(template.templateStatus)) {
				templates.set(template.templateStatus, []);
			}
			templates.get(template.templateStatus).push(template);
		}

		templates = new Map([...templates.entries()].sort());

		for (const [, items] of templates) {
			items.sort((a, b) => a.templateName.localeCompare(b.templateName))
		}
	}

	function selectTemplate() {
		message = loadTemplate(selectedTemplateId);

		if (message.templateReceived || message.templatePending) {
			External.Tools.showError(M.TEMPLATE_PENDING);
		} else if (message.templateRejected) {
			External.Tools.showError(M.TEMPLATE_REJECTED + message.rejectionReason);
		} else if (message.templateDisabled) {
			External.Tools.showError(M.TEMPLATE_DISABLED);
		}
	}

	function findMatchingTemplate(body, attachments) {
		if (templates) {
			for (const [, items] of templates) {
				const index = items.findIndex((template) => {
					return stringsMatchIgnoreTags(template.originalBody, body) && attachmentsMatch(template.originalAttachments, attachments);
				});
				if (index > -1) {
					return items[index].templateName;
				}
			}
		}
		return null;
	}

	const PROPERTY_TAGS = /\{[^}]*\}/g;

	function stringsMatchIgnoreTags(str1, str2) {
		return !!((str1 === str2) || (str1?.length && str2?.length && (str1.replace(PROPERTY_TAGS, '') === str2.replace(PROPERTY_TAGS, ''))));
	}

	function attachmentsMatch(att1, att2) {
		if (att1 === att2) {
			return true;
		}
		if ((!att1 || !att2) || (att1?.length !== att2?.length)) {
			return false;
		}
		for (let i = 0; i < att1.length; i++) {
			if (att1[i].imageBase !== att2[i].imageBase || att1[i].image !== att2[i].image) {
				return false;
			}
		}
		return true;
	}

	function loadTemplate(templateId) {
		if(templateId !== WILDCARD) {
			let index = -1;
			for (const [key, items] of templates) {
				index = items.findIndex((template) => {
					return template.templateId === templateId;
				});
				if (index > -1) {
					const template = templates.get(key)[index];
					template.body = template.originalBody;
					template.attachments = template.originalAttachments?.length ? [...template.originalAttachments] : null;
					return template;
				}
			}
		}
		const template = newEmptyMessage();
		if (templateId === WILDCARD) {
			template.templateId = WILDCARD;
		}
		return template;
	}

	function submitTemplate() {
		try {
			validateWhatsAppMessage(message);
		} catch (e) {
			External.Tools.showError(e);
			return;
		}

		const matchingTemplateName = findMatchingTemplate(message.body, message.attachments); //check if the entered message matches any other template so that we dont try submit a new template

		if (matchingTemplateName) {
			const msg = M.MATCHING_TEMPLATE_EXISTS.replace("#", matchingTemplateName);
			External.Tools.showError(msg);
			return;
		}

		let addTemplateEditor;

		External.Tools.showWindow({
			title: M.CREATE_NEW_TEMPLATE,
			icon: External.Popup.ICON_EDIT,
			width: 450,
			customClass: 'add-member-dialog',
			isModal: true,
			markup: '<div class="popup-newpage-wrapper bind-component"></div>',
			action: {
				onShow: function (popup, context) {
					addTemplateEditor = new AddWhatsAppTemplate({
						target: context.find('.bind-component')[0],
						props: {
							template: {
								name: message.templateName,
								category: message.templateCategory,
								language: message.templateLanguage
							}
						}
					});
				},
				onHide: function () {
					if (addTemplateEditor) {
						addTemplateEditor.$destroy();
						addTemplateEditor = null;
					}
				}
			},

			buttons: [
				External.Button(M.SUBMIT, External.Button.CLASS_MIDDLE_BLUE, function (popup) {
					const template = addTemplateEditor.getTemplate();

					try {
						validateTemplateName(template);
					} catch (e) {
						External.Tools.showError(e);
						return false;
					}

					message.templateName = template.name;
					message.templateCategory = template.category;
					message.templateLanguage = template.language;

					External.Tools.makeWebsiteRequest({
						action: 'createWhatsAppTemplate',
						method: 'post',
						data: {
							message: JSON.stringify(message)
						},
						onOk: async function (resp) {
							External.Tools.showAutohideMessage(5, M.TEMPLATE_SUBMITTED);

							await loadTemplates();
							selectedTemplateId = resp.data;

							hideTemplateSelect = false;

							if (enablePolling) {
								startPolling(selectedTemplateId);
							}
							popup.close();
						}
					});
				}),

				External.Button.MIDDLE_CANCEL
			]
		});
	}

	function startPolling(templateId) {
		External.Tools.showWindow({
			title: M.PLEASE_WAIT,
			showCloseButton: true,
			icon: External.Popup.ICON_QUESTION,
			width: 450,
			isModal: true,
			markup: External.WebPlatform.format(External.Templates.get('alertDialogMarkup'), 'message', M.WHATSAPP_TEMPLATE_APPROVAL_CHECK),
			action: {
				onShow: function (popup, context) {
					pollingTimer = window.setInterval(async () => {
						if (!pollingTimer) { //to avoid race errors
							return;
						}
						const status = await makeRequest('getWhatsAppTemplateStatus', {templateId: templateId}).catch((e) => {
							//stopPolling();
							//let the polling continue - subsequent attempts may work
							console.log("unable to get template status: " + JSON.stringify(e));
						});
						if (status?.name === External.WHATSAPP_TEMPLATE_STATUS.APPROVED || status?.name === External.WHATSAPP_TEMPLATE_STATUS.REJECTED) {
							stopPolling();

							await loadTemplates();
							if (templateId === selectedTemplateId) {
								selectTemplate();
							}

							popup.close()

							if (status.name === External.WHATSAPP_TEMPLATE_STATUS.APPROVED) {
								External.Tools.showAutohideMessage(5, M.TEMPLATE_APPROVED);
							}
						} else if (++pollCount === 4) {
							stopPolling();
							popup.close();

							External.Tools.showError(M.TEMPLATE_APPROVAL_TIMEOUT);
						}
					}, 15000);
				}
			},
			buttons: [
				External.Button(M.CLOSE, External.Button.CLASS_MIDDLE_BLUE, function (popup, context) {
					stopPolling();
					popup.close();
				}, External.Button.FLOAT_IGNORE)
			],
			customClass: External.Popup.CUSTOM_CLASS_PROMPT_DIALOG
		});
	}

	function stopPolling() {
		window.clearInterval(pollingTimer);
		pollingTimer = null;
	}

	function validateTemplateName(template) {
		if (!(/^[a-z0-9_]{1,450}$/.test(template.name))) { //only lowercase alphanumeric characters and underscores are allowed
			throw M.INVALID_TEMPLATE_NAME;
		}
		if (templates) {
			for (let [, items] of templates) {
				const index = items.findIndex((item) => {
					return item.templateName === template.name;
				});
				if (index > -1) {
					throw M.DUPLICATE_TEMPLATE_NAME;
				}
			}
		}
		if (template.category === '0') {
			throw M.SELECT_TEMPLATE_CATEGORY;
		}
	}

	function deleteTemplate() {
		if (freeFormMode && selectedTemplateId === WILDCARD) {
			return
		}
		External.Tools.prompt(M.DELETE_TEMPLATE, M.CONFIRM_TEMPLATE_DELETE, () => {
			External.Tools.makeWebsiteRequest({
				action: 'deleteWhatsAppTemplate',
				method: 'post',
				data: {
					templateId: message.templateId
				},
				onOk: () => {
					External.Tools.showAutohideMessage(5, M.TEMPLATE_DELETED);
					initNewMessage();
					selectedTemplateId = '0';
					loadTemplates();
				}
			});
		});
	}

	async function refreshTemplates() {
		await loadTemplates();
		selectTemplate();
		External.Tools.showAutohideMessage(5, M.TEMPLATE_LIST_REFRESHED);
	}

	function showHelp() {
		const url = External.Website.onboarding['booking'] || ''; //TODO

		if (url === '') {
			console.log("showHelp empty url");
			return;
		}

		const embedUrl = External.CPanel.getEmbedVideo(url);

		if (embedUrl !== '') {
			const title = M.HEY + ' ' + External.userName.split(' ')[0];
			const text = M.CHECK_VIDEO;

			External.Tools.showWindow({
				isModal: false,
				withDarkBackground: true,
				title: '',
				noNav: true,
				noTitle: true,
				markup: External.WebPlatform.format(External.Templates.get('introPopup'), 'titleclz', '', 'textclz', '',
					'title', title, 'text', text, 'video', embedUrl, 'clz', 'none'),
				icon: External.Popup.ICON_ADD,
				width: 850
			});
		}
	}

	function settingsConnected() {
		loadTemplates();
		isMessagingEnabled = true;
	}

	$: if (isMessagingEnabled) {

		charCount = message.body.length;

		const isFreeFormMsg = message.templateId === WILDCARD;

		if (isFreeFormMsg) {
			isSendAllowed = true;
			isNewTemplate = false;
		} else {
			const bodyUnchanged = stringsMatchIgnoreTags(message.body, message.originalBody);
			const attachmentUnchanged = attachmentsMatch(message.attachments, message.originalAttachments);

			isSendAllowed = message.templateApproved && bodyUnchanged && attachmentUnchanged;
			isNewTemplate = !(bodyUnchanged && attachmentUnchanged);
		}

		if (message.sendTime?.formattedTime && datePicker) {  //the datePicker is displayed if the message sendTime or id is set, but when setting the msg setEditorData function, the reaction does not happen until the function returns which causes an undefined error, so we do it here
			datePicker.value = message.sendTime.formattedTime;
		}

		if (onChange) {
			onChange(message);
		}
	}

	onMount(async () => {
		if (isMessagingEnabled) {
			await loadTemplates();
			if (message.templateId) {
				selectedTemplateId = message.templateId;
				hideTemplateSelect = false;
			}
		}

		if (freeFormMode) {
			selectedTemplateId = WILDCARD;
			selectTemplate();
		}
	});

	onDestroy(() => {
		window.clearInterval(pollingTimer);
	});

</script>

<style>
	.dynamic-popup-content label {
		width: max-content;
	}

	select optgroup {
		font-style: normal;
		font-weight: normal;
		/*font-size: 14px;*/
		color: #444;
	}

	select {
		text-indent: 10px;
	}

	option {
		text-indent: 10px;
		padding: 0px
	}
</style>

<div class='col20 f-left small-rules flex-1 flex-col justify-center' style='overflow-y: auto;' id='whatsAppEditor'>

	{#if !isMessagingEnabled}
		<div class='col20 f-right mb-20'>
			<div on:click={() => connectSettings(settingsConnected)} class="limit-warning-line flex align-center col20 cursor-pointer">
				<span class="material-icons flex-none">error</span>
				<div class="flex-1"><h4 class="col20 f-left"><u>{M.PLEASE_CONNECT_TWILIO}</u></h4>
				</div>
			</div>
		</div>
	{/if}

	{#if templates?.size}
		<div class='col20 f-right mt-5'>
			<a class='text-link-small t-right blue-text' on:click={() => hideTemplateSelect = !hideTemplateSelect}>
				{M.USE_EXISTING_TEMPLATE}
			</a>
		</div>

		<div class='col20 f-left mt-15' class:none={hideTemplateSelect}>
			<div class='info'>

				<div class='f-left'>
					<h5>{M.TEMPLATE}</h5>
					<span class='light-gray-text'>{M.SELECT_TEMPLATE_DESC}</span>
				</div>

				<div class='f-right' style=''>
					<a class='schedule-history-btn f-left rounded-effect button-tooltip' on:click={deleteTemplate}
						 data-tooltip='delete_whatsapp_template'>
						<svg viewBox='0 0 24 24' height='24' width='24' fill='none' xmlns='http://www.w3.org/2000/svg' stroke='#000000' stroke-width='0.384'>
							<g id='SVGRepo_bgCarrier' stroke-width='0'></g>
							<g id='SVGRepo_tracerCarrier' stroke-linecap='round' stroke-linejoin='round'></g>
							<g id='SVGRepo_iconCarrier'>
								<path fill-rule='evenodd' clip-rule='evenodd'
											d='M5.11686 7.7517C5.53016 7.72415 5.88754 8.03685 5.91509 8.45015L6.37503 15.3493C6.46489 16.6971 6.52892 17.6349 6.66948 18.3406C6.80583 19.025 6.99617 19.3873 7.26958 19.6431C7.54299 19.8989 7.91715 20.0647 8.60915 20.1552C9.32255 20.2485 10.2626 20.25 11.6134 20.25H12.3868C13.7376 20.25 14.6776 20.2485 15.391 20.1552C16.083 20.0647 16.4572 19.8989 16.7306 19.6431C17.004 19.3873 17.1943 19.025 17.3307 18.3406C17.4713 17.6349 17.5353 16.6971 17.6251 15.3493L18.0851 8.45015C18.1126 8.03685 18.47 7.72415 18.8833 7.7517C19.2966 7.77925 19.6093 8.13663 19.5818 8.54993L19.1183 15.5017C19.0328 16.7844 18.9638 17.8206 18.8018 18.6336C18.6334 19.4789 18.347 20.185 17.7554 20.7385C17.1638 21.2919 16.4402 21.5308 15.5856 21.6425C14.7635 21.7501 13.7251 21.7501 12.4395 21.75H11.5607C10.2751 21.7501 9.23664 21.7501 8.4146 21.6425C7.55995 21.5308 6.8364 21.2919 6.2448 20.7385C5.65321 20.185 5.36679 19.4789 5.19839 18.6336C5.03642 17.8205 4.96736 16.7844 4.88186 15.5017L4.41841 8.54993C4.39086 8.13663 4.70357 7.77925 5.11686 7.7517Z'
											fill='#000000'></path>
								<path fill-rule='evenodd' clip-rule='evenodd'
											d='M10.3553 2.25004L10.3094 2.25002C10.093 2.24988 9.90445 2.24976 9.72643 2.27819C9.02313 2.39049 8.41453 2.82915 8.08559 3.46084C8.00232 3.62074 7.94282 3.79964 7.87452 4.00496L7.86 4.04858L7.76291 4.33984C7.74392 4.39681 7.73863 4.41251 7.73402 4.42524C7.55891 4.90936 7.10488 5.23659 6.59023 5.24964C6.5767 5.24998 6.56013 5.25004 6.50008 5.25004H3.5C3.08579 5.25004 2.75 5.58582 2.75 6.00004C2.75 6.41425 3.08579 6.75004 3.5 6.75004L6.50865 6.75004L6.52539 6.75004H17.4748L17.4915 6.75004L20.5001 6.75004C20.9143 6.75004 21.2501 6.41425 21.2501 6.00004C21.2501 5.58582 20.9143 5.25004 20.5001 5.25004H17.5001C17.44 5.25004 17.4235 5.24998 17.4099 5.24964C16.8953 5.23659 16.4413 4.90933 16.2661 4.42522C16.2616 4.41258 16.2562 4.39653 16.2373 4.33984L16.1402 4.04858L16.1256 4.00494C16.0573 3.79961 15.9978 3.62073 15.9146 3.46084C15.5856 2.82915 14.977 2.39049 14.2737 2.27819C14.0957 2.24976 13.9072 2.24988 13.6908 2.25002L13.6448 2.25004H10.3553ZM9.14458 4.93548C9.10531 5.04404 9.05966 5.14902 9.00815 5.25004H14.992C14.9405 5.14902 14.8949 5.04405 14.8556 4.9355L14.8169 4.82216L14.7171 4.52292C14.626 4.2494 14.605 4.19363 14.5842 4.15364C14.4745 3.94307 14.2716 3.79686 14.0372 3.75942C13.9927 3.75231 13.9331 3.75004 13.6448 3.75004H10.3553C10.067 3.75004 10.0075 3.75231 9.96296 3.75942C9.72853 3.79686 9.52566 3.94307 9.41601 4.15364C9.39519 4.19363 9.37419 4.24942 9.28302 4.52292L9.18322 4.82234C9.1682 4.86742 9.1565 4.90251 9.14458 4.93548Z'
											fill='#000000'></path>
							</g>
						</svg>
					</a>

					<a class='schedule-history-btn f-left ml-5 rounded-effect button-tooltip' on:click={refreshTemplates}
						 data-tooltip='refresh_whatsapp_template'>
						<svg viewBox='0 0 24 24' height='24' width='24' fill='none' xmlns='http://www.w3.org/2000/svg' stroke='#000000'
								 stroke-width='0.4800000000000001'>
							<g id='SVGRepo_bgCarrier' stroke-width='0'></g>
							<g id='SVGRepo_tracerCarrier' stroke-linecap='round' stroke-linejoin='round'></g>
							<g id='SVGRepo_iconCarrier'>
								<path
									d='M12.0789 2.25C7.2854 2.25 3.34478 5.913 2.96055 10.5833H2.00002C1.69614 10.5833 1.42229 10.7667 1.30655 11.0477C1.19081 11.3287 1.25606 11.6517 1.47178 11.8657L3.15159 13.5324C3.444 13.8225 3.91567 13.8225 4.20808 13.5324L5.88789 11.8657C6.10361 11.6517 6.16886 11.3287 6.05312 11.0477C5.93738 10.7667 5.66353 10.5833 5.35965 10.5833H4.4668C4.84652 6.75167 8.10479 3.75 12.0789 3.75C14.8484 3.75 17.2727 5.20845 18.6156 7.39279C18.8325 7.74565 19.2944 7.85585 19.6473 7.63892C20.0002 7.42199 20.1104 6.96007 19.8934 6.60721C18.2871 3.99427 15.3873 2.25 12.0789 2.25Z'
									fill='#000000'></path>
								<path
									d='M20.8411 10.4666C20.549 10.1778 20.0789 10.1778 19.7867 10.4666L18.1005 12.1333C17.8841 12.3471 17.8184 12.6703 17.9339 12.9517C18.0495 13.233 18.3235 13.4167 18.6277 13.4167H19.5268C19.1455 17.2462 15.8759 20.25 11.8828 20.25C9.10026 20.25 6.66586 18.7903 5.31796 16.6061C5.10042 16.2536 4.63833 16.1442 4.28583 16.3618C3.93334 16.5793 3.82393 17.0414 4.04146 17.3939C5.65407 20.007 8.56406 21.75 11.8828 21.75C16.6906 21.75 20.6475 18.0892 21.0331 13.4167H22.0002C22.3043 13.4167 22.5783 13.233 22.6939 12.9517C22.8095 12.6703 22.7437 12.3471 22.5274 12.1333L20.8411 10.4666Z'
									fill='#000000'></path>
							</g>
						</svg>
					</a>

					<a class='schedule-history-btn f-left ml-5 rounded-effect stats-info'>
						<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="#000000">
							<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
							<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
							<g id="SVGRepo_iconCarrier">
								<g id="Layer_2" data-name="Layer 2">
									<g id="invisible_box" data-name="invisible box">
										<rect width="48" height="48" fill="none"></rect>
									</g>
									<g id="Icons">
										<g>
											<path
												d="M24.3,6A11.2,11.2,0,0,0,16,9.3a11,11,0,0,0-3.5,8.2,2.5,2.5,0,0,0,5,0,6.5,6.5,0,0,1,2-4.7A6.2,6.2,0,0,1,24.2,11a6.5,6.5,0,0,1,1,12.9,4.4,4.4,0,0,0-3.7,4.4v3.2a2.5,2.5,0,0,0,5,0V28.7a11.6,11.6,0,0,0,9-11.5A11.7,11.7,0,0,0,24.3,6Z"></path>
											<circle cx="24" cy="39.5" r="2.5"></circle>
										</g>
									</g>
								</g>
							</g>
						</svg>

						<div class="none-soft hidden-info" style="left: auto; right: 30px; top: 30px;">
							<h4>{M.WHATSAPP_TEMPLATES_TITLE}</h4>
							<span>{M.WHATSAPP_TEMPLATES_DESC}</span>
						</div>
					</a>
				</div>
			</div>

			<select class='page-select' bind:value={selectedTemplateId} on:change={selectTemplate} style='font-family: inherit, Font-Awesome;'>
				<option value='0' disabled>{M.PLEASE_SELECT}</option>
				{#if freeFormMode}
					<option value='{WILDCARD}'>&#128172;&nbsp;&nbsp;{M.NEW_EMPTY_MESSAGE}</option>
				{/if}
				{#each [...templates] as [key, value]}
					<optgroup label='&#128193;&nbsp;&nbsp;{TEMPLATE_STATUS_LABELS[key].label}'>
						{#each value as template}
							<option value='{template.templateId}' style='padding-left: -20px; margin-left: -20px; text-indent: -20px'>
								{#if template.attachments?.length}
									&#128206;
								{:else}
									&#128172;
								{/if}
								&nbsp;&nbsp;{template.templateName} ({template.templateLanguage})
							</option>
						{/each}
					</optgroup>
				{/each}
			</select>
		</div>
	{/if}

	{#key isMessagingEnabled}
		<div class='single-message-textarea f-left col20 rich-text-area' style='padding-bottom: 0px'>
			<textarea bind:this={textarea} bind:value={message.body} maxlength='{getChannelMessageMaxLength(External.CRM_MESSAGE_CHANNEL.WHATSAPP)}'
								style='line-height: 23px; height: 100%; min-height: 152px; max-width: 560px; border-radius: 3px; margin-top: 9px; margin-bottom: 14px'
								placeholder={M.TYPE_YOUR_MESSAGE}></textarea>  <!--14px bottom margin is as per the email editor styling-->
		</div>
	{/key}

	<div class='col20 f-left'>
		<a on:click={selectProperty} class='f-left rounded-effect inline-block single-email-schedule-button'>
			<span class='flex align-center'>
				<svg viewBox='0 0 24 24' height='24' width='24' fill='none' xmlns='http://www.w3.org/2000/svg'><g id='SVGRepo_bgCarrier' stroke-width='0'></g><g
					id='SVGRepo_tracerCarrier' stroke-linecap='round' stroke-linejoin='round'></g><g id='SVGRepo_iconCarrier'> <path
					d='M8.00005 9C8.55233 9 9.00005 8.55228 9.00005 8C9.00005 7.44772 8.55233 7 8.00005 7C7.44776 7 7.00005 7.44772 7.00005 8C7.00005 8.55228 7.44776 9 8.00005 9Z'
					fill='#0F1729'></path> <path fill-rule='evenodd' clip-rule='evenodd'
																			 d='M12.1635 2.05864C11.6504 1.962 11.1228 2.00288 10.4302 2.05655L7.49854 2.2821C6.80865 2.33516 6.23449 2.37931 5.76481 2.4522C5.27385 2.52839 4.82331 2.64559 4.39819 2.88284C3.76237 3.23769 3.23773 3.76232 2.88289 4.39814C2.64563 4.82326 2.52844 5.2738 2.45225 5.76476C2.37936 6.23444 2.3352 6.8086 2.28214 7.49849L2.05659 10.4301C2.00292 11.1228 1.96204 11.6504 2.05869 12.1635C2.14036 12.597 2.2945 13.0137 2.51464 13.396C2.77515 13.8484 3.14954 14.2224 3.64107 14.7134L8.65467 19.7269C9.16802 20.2403 9.59268 20.665 9.96589 20.9856C10.3535 21.3187 10.7457 21.5938 11.2112 21.7632C12.0788 22.0789 13.0298 22.0789 13.8974 21.7632C14.3629 21.5938 14.7551 21.3187 15.1427 20.9856C15.5159 20.665 15.9406 20.2403 16.4539 19.7269L19.727 16.4539C20.2403 15.9405 20.665 15.5158 20.9857 15.1426C21.3187 14.755 21.5938 14.3628 21.7632 13.8973C22.079 13.0298 22.079 12.0787 21.7632 11.2112C21.5938 10.7457 21.3187 10.3535 20.9857 9.96584C20.665 9.59262 20.2403 9.16794 19.7269 8.65458L14.7134 3.64103C14.2225 3.1495 13.8485 2.7751 13.396 2.51459C13.0137 2.29445 12.597 2.14031 12.1635 2.05864ZM10.4701 4.05943C11.327 3.99351 11.5732 3.98263 11.7933 4.02408C12.006 4.06415 12.2105 4.13978 12.3981 4.2478C12.5921 4.35952 12.772 4.52802 13.3797 5.13578L18.2859 10.042C18.8329 10.589 19.2033 10.9603 19.4687 11.2692C19.7274 11.5702 19.8313 11.751 19.8838 11.8952C20.0388 12.3209 20.0388 12.7876 19.8838 13.2133C19.8313 13.3575 19.7274 13.5382 19.4687 13.8393C19.2033 14.1482 18.8329 14.5195 18.2859 15.0665L15.0666 18.2859C14.5196 18.8328 14.1482 19.2032 13.8393 19.4687C13.5383 19.7273 13.3575 19.8313 13.2133 19.8838C12.7876 20.0387 12.321 20.0387 11.8953 19.8838C11.751 19.8313 11.5703 19.7273 11.2693 19.4687C10.9603 19.2032 10.589 18.8328 10.042 18.2859L5.13583 13.3797C4.52807 12.7719 4.35957 12.592 4.24785 12.398C4.13983 12.2104 4.0642 12.006 4.02412 11.7932C3.98268 11.5732 3.99356 11.327 4.05948 10.47L4.27333 7.68986C4.33007 6.95232 4.36897 6.45566 4.42859 6.07147C4.48624 5.69996 4.55341 5.50883 4.62932 5.37281C4.80344 5.06082 5.06087 4.80339 5.37286 4.62928C5.50888 4.55336 5.7 4.4862 6.07151 4.42854C6.45571 4.36892 6.95237 4.33002 7.6899 4.27329L10.4701 4.05943Z'
																			 fill='#0F1729'></path> </g></svg>
				{M.ADD_TAGS}
			</span>
		</a>

		<a on:click={enableAttachments} class:blue={!!message.attachments} class='f-left ml-5 rounded-effect inline-block single-email-schedule-button'>
			<span class='flex align-center'>
				<svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'>
					<path
						d='M120-200q-33 0-56.5-23.5T40-280v-400q0-33 23.5-56.5T120-760h400q33 0 56.5 23.5T600-680v400q0 33-23.5 56.5T520-200H120Zm600-320q-17 0-28.5-11.5T680-560v-160q0-17 11.5-28.5T720-760h160q17 0 28.5 11.5T920-720v160q0 17-11.5 28.5T880-520H720Zm40-80h80v-80h-80v80ZM120-280h400v-400H120v400Zm40-80h320L375-500l-75 100-55-73-85 113Zm560 160q-17 0-28.5-11.5T680-240v-160q0-17 11.5-28.5T720-440h160q17 0 28.5 11.5T920-400v160q0 17-11.5 28.5T880-200H720Zm40-80h80v-80h-80v80Zm-640 0v-400 400Zm640-320v-80 80Zm0 320v-80 80Z'></path>
				</svg>
				{M.ATTACH}
			</span>
		</a>

		{#if isSendAllowed}
			<a on:click={sendTest} class='f-left ml-5 rounded-effect inline-block single-email-schedule-button'>
				<span class='flex align-center'>
					<svg viewBox='0 0 16 16' height='24' width='24' xmlns='http://www.w3.org/2000/svg' fill='#000000'><g id='SVGRepo_bgCarrier'
																																																							 stroke-width='0'></g><g
						id='SVGRepo_tracerCarrier' stroke-linecap='round' stroke-linejoin='round'></g><g id='SVGRepo_iconCarrier'> <rect width='16' height='16'
																																																														 id='icon-bound'
																																																														 fill='none'></rect> <path
						d='M2,9.014L3.414,7.6L6.004,10.189L12.593,3.6L14.007,5.014L6.003,13.017L2,9.014Z'></path> </g></svg>
					{M.SEND_TEST_MESSAGE}
				</span>
			</a>
		{/if}

		{#if enableScheduling}
			{#if !message.id}
				<a on:click={message.sendTime ? hideDatePicker : showDatePicker} class:blue={!!message.sendTime}
					 class='f-left ml-5 rounded-effect inline-block single-email-schedule-button'>
							<span class='flex align-center'>
								<svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'><path
									d='M200-640h560v-80H200v80Zm0 0v-80 80Zm0 560q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v227q-19-9-39-15t-41-9v-43H200v400h252q7 22 16.5 42T491-80H200Zm520 40q-83 0-141.5-58.5T520-240q0-83 58.5-141.5T720-440q83 0 141.5 58.5T920-240q0 83-58.5 141.5T720-40Zm67-105 28-28-75-75v-112h-40v128l87 87Z'/></svg>
								{M.SCHEDULE_EMAIL_BTN}
							</span>
				</a>
			{/if}

			<a class='schedule-history-btn f-left ml-5 rounded-effect button-tooltip' on:click={showScheduledMessages}
				 data-tooltip='view_scheduled_messages'>
				<svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24'>
					<path
						d='m787-145 28-28-75-75v-112h-40v128l87 87Zm-587 25q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v268q-19-9-39-15.5t-41-9.5v-243H200v560h242q3 22 9.5 42t15.5 38H200Zm0-120v40-560 243-3 280Zm80-40h163q3-21 9.5-41t14.5-39H280v80Zm0-160h244q32-30 71.5-50t84.5-27v-3H280v80Zm0-160h400v-80H280v80ZM720-40q-83 0-141.5-58.5T520-240q0-83 58.5-141.5T720-440q83 0 141.5 58.5T920-240q0 83-58.5 141.5T720-40Z'/>
				</svg>
			</a>
		{/if}

		<span class='f-right contact-message-char-counter'>
			<span class='flex align-center'>
				{charCount}
			</span>
		</span>
	</div>

	{#key message.attachments}
		{#if message.attachments?.length}
			<div class='col20 f-left mt-15'>
				<div class='info'>
					<h5>{M.ATTACH_IMAGE}</h5>
					<span class='light-gray-text'>
            {M.ATTACH_IMAGE_DESC}
          </span>
				</div>
				<div style='margin-left: 3px' class='whatsapp-image-picker'>
					{#each message.attachments as attachment, i}
						<ImagePicker bind:imageBase={attachment.base} bind:image={attachment.name} bind:enabled={isMessagingEnabled} onChange={() => setAttachmentUrl(attachment)} />
					{/each}
				</div>
			</div>
		{/if}
	{/key}

	{#if message.sendTime || message.id}
		<div class='col20 f-left mt-15'>
			<div class='info'>
				<h5>{M.SEND_DATE}</h5>
				<span class='light-gray-text'>
							{M.SELECT_MESSAGE_SEND_DATE_DESC}
						</span>
			</div>
			<DateTimePicker bind:instance={datePicker} bind:value={message.sendTime} onChange='{dateChanged}' readonly={true}/>
		</div>
	{/if}

</div>

<slot name='footerButtons' {message} {isSendAllowed} {sendNow} {scheduleMessage} {sendScheduledNow} {isNewTemplate} {submitTemplate} {cancel}/>
